import { FlexyForm, FormStructure } from '@europrocurement/flexy-form';
import { useFormaliteService } from '@europrocurement/l2d-domain';
import React from 'react';

import {
    ReglementsEtAcomptesCBJsonldAcompteCreate,
    TblReglement2DetailsJsonldAcompteCreate,
} from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { useToaster } from '@europrocurement/l2d-modules/hooks';
import { useForm } from 'react-hook-form';
import { Backdrop, Box, CircularProgress, SxProps } from '@mui/material';

export type ProvisionAddType = {
    collectionId: number;
    afterSubmit: () => void;
    overwriteDefaultProvisionAdd?: Partial<ProvisionAddFormObject>;
    sx?: SxProps;
};

export type EncaissementOption = {
    label: string;
    value: 'CH' | 'ESP' | 'VIR';
};

export type ProvisionAddFormObject = {
    mode: EncaissementOption;
    montant: string;
    date?: Date;
    reference?: string;
    note?: string;
    // Pas besoin de attachment dans le form, ça sera géré par useUploader
};

const ProvisionAdd: React.FunctionComponent<ProvisionAddType> = function ({
    collectionId,
    afterSubmit,
    overwriteDefaultProvisionAdd,
    sx,
}) {
    const L2D_FORMALITE_ORIGIN = 13;
    const [isLoading, setIsLoading] = React.useState(false);

    const { acomptesApi } = useFormaliteService();

    const defaultProvisionAdd: Partial<ProvisionAddFormObject> = {
        mode: {
            label: 'Virement bancaire',
            value: 'VIR',
        },
        montant: '0',
        date: new Date(),
        reference: undefined,
        note: undefined,
        ...overwriteDefaultProvisionAdd,
    };

    const formContext = useForm({
        mode: 'onTouched',
        defaultValues: defaultProvisionAdd,
    });

    const formStructure: FormStructure[] = React.useMemo(
        () => [
            {
                type: 'header',
                label: 'Ajouter un encaissement',
                name: 'main_informations',
            },
            {
                type: 'select',
                name: 'mode',
                inputlabel: "Mode d'encaissement",
                options: [
                    {
                        label: 'Chèque',
                        value: 'CH',
                    },
                    {
                        label: 'Espèces',
                        value: 'ESP',
                    },
                    {
                        label: 'Virement bancaire',
                        value: 'VIR',
                    },
                ],
                placeholder: "Veuillez sélectionner un mode d'encaissement",
                rules: {
                    required: "Vous devez sélectionner un mode d'encaissement",
                },
                xs: 12,
                sm: 12,
                md: 12,
                lg: 12,
            },
            {
                type: 'number',
                inputProps: {
                    step: 1,
                    min: 0,
                },
                name: 'montant',
                inputlabel: 'Montant (€)',
                placeholder: '0.00',
                rules: {
                    required: 'Vous devez renseigner un montant',
                },
                xs: 12,
                sm: 12,
                md: 12,
                lg: 12,
            },
            {
                type: 'date',
                name: 'date',
                inputlabel: 'Date',
                disableFuture: true,
                xs: 12,
                sm: 12,
                md: 12,
                lg: 12,
            },
            {
                type: 'text',
                name: 'reference',
                inputlabel: 'Référence',
                placeholder: 'REF001',
                xs: 12,
                sm: 12,
                md: 12,
                lg: 12,
            },
            {
                type: 'text',
                name: 'note',
                inputlabel: 'Note',
                placeholder: 'Note de la transaction',
                xs: 12,
                sm: 12,
                md: 12,
                lg: 12,
            },
        ],
        [],
    );

    const { handleApiResponse } = useToaster();

    const valueOrUndefined = (fieldToTest?: string | null): string | undefined =>
        fieldToTest !== undefined && fieldToTest !== null && fieldToTest !== ''
            ? (fieldToTest as unknown as string)
            : undefined;

    const onSubmit = async () => {
        setIsLoading(true);

        try {
            const detailsReglements: TblReglement2DetailsJsonldAcompteCreate = {
                idCollection: collectionId as unknown as string,
                ttc: formContext.getValues().montant as unknown as string,
            };

            const reglementsEtAcomptesCBJsonldAcompteCreate: ReglementsEtAcomptesCBJsonldAcompteCreate =
                {
                    mode: formContext.getValues().mode?.value as unknown as string,
                    montant: formContext.getValues().montant as unknown as string,
                    reference: valueOrUndefined(
                        formContext.getValues().reference as unknown as string,
                    ),
                    origine: L2D_FORMALITE_ORIGIN,
                    detailsReglements: [detailsReglements],
                    note: valueOrUndefined(formContext.getValues().note as unknown as string),
                    dreception: valueOrUndefined(formContext.getValues().date as unknown as string),
                    usercreation: 42, // TODO LLC
                };

            // 💰 Créer l'encaissement
            console.debug("💰 Création de l'encaissement...");
            const createRequest = acomptesApi.apiAcomptesPost({
                reglementsEtAcomptesCBJsonldAcompteCreate,
            });

            await handleApiResponse(createRequest, () => {});

            console.debug('✅ Encaissement créé avec succès');
            afterSubmit();
        } catch (error) {
            console.error("❌ Erreur lors de la création de l'encaissement:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const formProps = {
        formContext,
        onSubmit,
        formObject: {},
    };

    return (
        <Box sx={{ ...sx }}>
            <Backdrop
                sx={(backDropTheme) => ({ color: '#fff', zIndex: backDropTheme.zIndex.drawer + 1 })}
                open={isLoading}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
            <FlexyForm
                formStructure={formStructure}
                {...formProps}
            />
        </Box>
    );
};

export default ProvisionAdd;
